// Today / Home — the actual landing dashboard. Replaces the previous redirect
// straight to /inbox.
//
// Composition:
//   - Greeting banner (good morning/afternoon, your name, today's date)
//   - Approvals strip (only when pending count > 0)
//   - 6-column KPI ticker with deltas
//   - 3-column hot lists: replies, top contacts, deals (or dormant hot when no deals)
//   - Expiring contracts call-out

import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  Inbox, Users, Sparkles, Briefcase, FileSignature, ArrowRight,
  TrendingUp, AlertCircle, Mail, Activity, Clock, Building2,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { homeApi } from "@/lib/api";
import { cn, initials, avatarColor } from "@/lib/utils";
import { fmtMoneyDollars, fmtRelative, fmtNumber, pillVariant } from "@/lib/format";
import { useTitle } from "@/hooks/useTitle";

const greetingFor = (hour: number) =>
  hour < 5 ? "Working late" : hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

const todayLabel = () =>
  new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

export default function HomeRoute() {
  useTitle("Today");
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ["home"],
    queryFn: () => homeApi.get(),
    refetchInterval: 60_000,
  });

  return (
    <div className="h-full overflow-y-auto bg-surface scrollbar-thin">
      <div className="mx-auto max-w-7xl px-6 py-6">
        {/* Greeting */}
        <header className="mb-5 flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-ink">
              {data ? `${greetingFor(data.greeting.hour)}, ${data.greeting.name}` : "Hello"}
            </h1>
            <p className="mt-0.5 text-sm text-ink-muted">{todayLabel()} · LuzGrid CRM</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => navigate("/inbox")}>
              <Inbox className="h-3.5 w-3.5" /> Open inbox
            </Button>
            <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => navigate("/contracts/templates")}>
              <FileSignature className="h-3.5 w-3.5" /> New contract
            </Button>
            <Button size="sm" className="gap-1.5" onClick={() => navigate("/contacts")}>
              <Users className="h-3.5 w-3.5" /> Contacts
            </Button>
          </div>
        </header>

        {error && (
          <div className="mb-4 rounded-lg border border-danger/30 bg-danger/10 p-3 text-sm text-danger">
            Couldn't load dashboard: {(error as Error).message}
          </div>
        )}

        {/* Approvals banner */}
        {data && data.kpis.pending_approvals > 0 && (
          <Link
            to="/approvals"
            className="mb-4 flex items-center gap-3 rounded-lg border border-warn/30 bg-warn/10 px-4 py-3 transition-colors hover:bg-warn/15"
          >
            <Sparkles className="h-5 w-5 text-warn shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-ink">
                {data.kpis.pending_approvals} approval{data.kpis.pending_approvals === 1 ? "" : "s"} pending review
              </p>
              <p className="text-xs text-ink-muted">
                Engagement-triggered follow-ups waiting for your green-light.
              </p>
            </div>
            <Button size="sm" variant="ghost" className="shrink-0">
              Review queue <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </Link>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          <KpiTile
            icon={<Users className="h-3.5 w-3.5" />}
            label="Contacts"
            value={data?.kpis.contacts_total}
            sub={data?.kpis.contacts_new_7d ? `+${data.kpis.contacts_new_7d} this week` : "in CRM"}
            trend={data?.kpis.contacts_new_7d ? "up" : "flat"}
            isLoading={isLoading}
            onClick={() => navigate("/contacts")}
          />
          <KpiTile
            icon={<Building2 className="h-3.5 w-3.5" />}
            label="Companies"
            value={data?.kpis.companies_total}
            sub="mirrored"
            isLoading={isLoading}
            onClick={() => navigate("/companies")}
          />
          <KpiTile
            icon={<Mail className="h-3.5 w-3.5" />}
            label="Replies (7d)"
            value={data?.kpis.replies_7d}
            sub={data?.kpis.replies_7d ? "follow up" : "from outreach"}
            trend={data?.kpis.replies_7d ? "up" : "flat"}
            highlight={!!data?.kpis.replies_7d}
            isLoading={isLoading}
            onClick={() => navigate("/inbox")}
          />
          <KpiTile
            icon={<Activity className="h-3.5 w-3.5" />}
            label="In sequences"
            value={data?.kpis.active_enrollments}
            sub="active"
            isLoading={isLoading}
            onClick={() => navigate("/sequences")}
          />
          <KpiTile
            icon={<Sparkles className="h-3.5 w-3.5" />}
            label="Approvals"
            value={data?.kpis.pending_approvals}
            sub={data?.kpis.pending_approvals ? "needs review" : "all clear"}
            trend={data?.kpis.pending_approvals ? "warn" : "flat"}
            isLoading={isLoading}
            onClick={() => navigate("/approvals")}
          />
          <KpiTile
            icon={<Clock className="h-3.5 w-3.5" />}
            label="Tasks overdue"
            value={data?.kpis.tasks_overdue}
            sub="overdue"
            trend={data?.kpis.tasks_overdue ? "danger" : "flat"}
            isLoading={isLoading}
            onClick={() => navigate("/tasks?tab=overdue")}
          />
        </div>

        {/* Hot lists row */}
        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Hot threads */}
          <Card>
            <CardHeader className="flex-row items-center justify-between gap-2 pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Mail className="h-4 w-4 text-accent-purple" />
                Latest replies
              </CardTitle>
              <Link to="/inbox" className="text-xs text-brand-deep hover:underline">
                Inbox →
              </Link>
            </CardHeader>
            <CardContent className="px-2 pb-2">
              {isLoading ? (
                <ListSkeleton count={3} />
              ) : data?.hot_threads.length ? (
                data.hot_threads.map((t) => (
                  <Link
                    key={t.id}
                    to={`/inbox/${encodeURIComponent(t.id)}`}
                    className="flex items-center gap-2 rounded px-2 py-2 transition-colors hover:bg-surface-hover"
                  >
                    <Avatar className="h-7 w-7 shrink-0">
                      <AvatarFallback className={avatarColor(t.contact_email ?? t.contact_name)}>
                        {initials(t.contact_name ?? t.contact_email)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink">
                        {t.contact_name?.trim() || t.contact_email || "Unknown"}
                      </p>
                      <p className="truncate text-[11px] text-ink-dim">
                        replied · {t.subject ?? "(no subject)"}
                      </p>
                    </div>
                    <span className="shrink-0 text-[10px] text-ink-dim">
                      {fmtRelative(t.last_message_at)}
                    </span>
                  </Link>
                ))
              ) : (
                <EmptyMini icon={<Mail className="h-4 w-4" />} text="No new replies yet" />
              )}
            </CardContent>
          </Card>

          {/* Top contacts */}
          <Card>
            <CardHeader className="flex-row items-center justify-between gap-2 pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingUp className="h-4 w-4 text-brand" />
                Top contacts
              </CardTitle>
              <Link to="/contacts" className="text-xs text-brand-deep hover:underline">
                All contacts →
              </Link>
            </CardHeader>
            <CardContent className="px-2 pb-2">
              {isLoading ? (
                <ListSkeleton count={3} />
              ) : data?.hot_contacts.length ? (
                data.hot_contacts.map((c) => (
                  <Link
                    key={c.id}
                    to={`/contacts/${c.id}`}
                    className="flex items-center gap-2 rounded px-2 py-2 transition-colors hover:bg-surface-hover"
                  >
                    <Avatar className="h-7 w-7 shrink-0">
                      <AvatarFallback className={avatarColor(c.email)}>
                        {initials(c.full_name ?? c.email)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink">
                        {c.full_name?.trim() || c.email}
                      </p>
                      <p className="truncate text-[11px] text-ink-dim">{c.company_name ?? "—"}</p>
                    </div>
                    <ScoreBadge score={c.rule_score} bucket={c.score_bucket} />
                  </Link>
                ))
              ) : (
                <EmptyMini icon={<Users className="h-4 w-4" />} text="No scored contacts yet" />
              )}
            </CardContent>
          </Card>

          {/* Hot deals OR dormant hot contacts */}
          <Card>
            <CardHeader className="flex-row items-center justify-between gap-2 pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                {data?.hot_deals.length ? (
                  <>
                    <Briefcase className="h-4 w-4 text-success" /> Pipeline focus
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-4 w-4 text-warn" /> Re-touch today
                  </>
                )}
              </CardTitle>
              <Link
                to={data?.hot_deals.length ? "/deals" : "/contacts"}
                className="text-xs text-brand-deep hover:underline"
              >
                {data?.hot_deals.length ? "All deals →" : "Contacts →"}
              </Link>
            </CardHeader>
            <CardContent className="px-2 pb-2">
              {isLoading ? (
                <ListSkeleton count={3} />
              ) : data?.hot_deals.length ? (
                data.hot_deals.map((d) => (
                  <Link
                    key={d.id}
                    to="/deals"
                    className="flex items-center gap-2 rounded px-2 py-2 transition-colors hover:bg-surface-hover"
                  >
                    <span
                      className="h-7 w-1 shrink-0 rounded-full"
                      style={{ backgroundColor: d.stage_color ?? "#94a3b8" }}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink">{d.name}</p>
                      <p className="truncate text-[11px] text-ink-dim">
                        {d.company_name ?? "—"} · {d.stage_name ?? "—"} · {d.days_in_stage ?? 0}d
                      </p>
                    </div>
                    <span className="shrink-0 text-sm font-semibold tabular-nums text-brand-deep">
                      {fmtMoneyDollars(d.amount_usd)}
                    </span>
                  </Link>
                ))
              ) : data?.dormant_hot_contacts.length ? (
                data.dormant_hot_contacts.map((c) => {
                  const days = c.last_emailed_at
                    ? Math.floor((Date.now() - Date.parse(c.last_emailed_at)) / 86_400_000)
                    : null;
                  return (
                    <Link
                      key={c.id}
                      to={`/contacts/${c.id}`}
                      className="flex items-center gap-2 rounded px-2 py-2 transition-colors hover:bg-surface-hover"
                    >
                      <Avatar className="h-7 w-7 shrink-0">
                        <AvatarFallback className={avatarColor(c.email)}>
                          {initials(c.full_name ?? c.email)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-ink">
                          {c.full_name?.trim() || c.email}
                        </p>
                        <p className="truncate text-[11px] text-ink-dim">
                          {c.company_name ?? "—"} ·{" "}
                          {days != null ? `quiet ${days}d` : "never emailed"}
                        </p>
                      </div>
                      <ScoreBadge score={c.rule_score} bucket={null} />
                    </Link>
                  );
                })
              ) : (
                <EmptyMini icon={<Briefcase className="h-4 w-4" />} text="No deals or dormant hot contacts" />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Expiring contracts */}
        {data && data.expiring_contracts.length > 0 && (
          <Card className="mt-4">
            <CardHeader className="flex-row items-center justify-between gap-2 pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <FileSignature className="h-4 w-4 text-warn" />
                Expiring in next 30 days
              </CardTitle>
              <Link to="/contracts?tab=expiring" className="text-xs text-brand-deep hover:underline">
                All expiring →
              </Link>
            </CardHeader>
            <CardContent className="px-2 pb-2">
              {data.expiring_contracts.map((k) => (
                <Link
                  key={k.id}
                  to={`/contracts/${k.id}`}
                  className="flex items-center gap-2 rounded px-2 py-2 transition-colors hover:bg-surface-hover"
                >
                  <FileSignature className="h-4 w-4 shrink-0 text-ink-dim" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">{k.title}</p>
                    <p className="truncate text-[11px] text-ink-dim">{k.company_name ?? "—"}</p>
                  </div>
                  <span className="shrink-0 text-xs text-warn">expires {fmtRelative(k.expires_at)}</span>
                </Link>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function KpiTile({
  icon, label, value, sub, trend = "flat", highlight, isLoading, onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | undefined;
  sub: string;
  trend?: "up" | "flat" | "warn" | "danger";
  highlight?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
}) {
  const trendColor =
    trend === "up" ? "text-success" :
    trend === "warn" ? "text-warn" :
    trend === "danger" ? "text-danger" :
    "text-ink-dim";
  return (
    <button
      onClick={onClick}
      className={cn(
        "rounded-lg border bg-surface-panel p-3 text-left shadow-sm transition-all hover:border-brand/40 hover:shadow-md",
        highlight ? "border-brand/30 bg-brand/5" : "border-line",
      )}
    >
      <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-ink-dim">
        <span className="flex items-center gap-1">{icon} {label}</span>
      </div>
      <div className="mt-1.5 flex items-baseline gap-2">
        {isLoading ? (
          <Skeleton className="h-7 w-16" />
        ) : (
          <span className="font-display text-2xl font-bold tabular-nums text-ink">
            {fmtNumber(value)}
          </span>
        )}
      </div>
      <p className={cn("mt-0.5 text-[11px]", trendColor)}>{sub}</p>
    </button>
  );
}

function ScoreBadge({ score, bucket }: { score: number | null; bucket: string | null }) {
  if (score == null) return <span className="text-xs text-ink-dim">—</span>;
  return (
    <Badge variant={pillVariant(bucket ?? "")} className="tabular-nums">
      {score}
    </Badge>
  );
}

function ListSkeleton({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-2 px-2 py-2">
          <Skeleton className="h-7 w-7 rounded-full" />
          <div className="flex-1 space-y-1">
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </>
  );
}

function EmptyMini({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-2 px-2 py-6 text-xs text-ink-dim">
      {icon} {text}
    </div>
  );
}
